"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { useTranslation } from "react-i18next";
import { Clock, AlertTriangle } from "lucide-react";

import { cn } from "@/lib/utils";

interface RateLimitIndicatorProps {
  level: "normal" | "warning" | "restricted" | "severe";
  cooldownEnd: number | null;
  remainingCooldown: number;
  canSend: boolean;
  timeUntilNextMessage?: number;
  className?: string;
}

export function RateLimitIndicator({
  level,
  cooldownEnd,
  remainingCooldown,
  canSend,
  timeUntilNextMessage,
  className,
}: RateLimitIndicatorProps) {
  const { t } = useTranslation(["chat", "common"]);
  const [timeLeft, setTimeLeft] = useState(0);

  // Update countdown timer
  useEffect(() => {
    if (timeUntilNextMessage) {
      setTimeLeft(timeUntilNextMessage);
      const interval = setInterval(() => {
        setTimeLeft((prev) => {
          const newTime = Math.max(0, prev - 1);

          if (newTime === 0) {
            clearInterval(interval);
          }

          return newTime;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [timeUntilNextMessage]);

  // Don't show anything if user can send normally
  if (level === "normal" && canSend && !cooldownEnd) {
    return null;
  }

  const getLevelInfo = () => {
    switch (level) {
      case "warning":
        return {
          color: "text-yellow-500",
          bgColor: "bg-yellow-500/10",
          borderColor: "border-yellow-500/30",
          icon: AlertTriangle,
          title: t("chat:rateLimiting.slowDown"),
          description: t("chat:rateLimiting.limits.warning"),
        };
      case "restricted":
        return {
          color: "text-orange-500",
          bgColor: "bg-orange-500/10",
          borderColor: "border-orange-500/30",
          icon: AlertTriangle,
          title: t("chat:rateLimiting.rateLimited"),
          description: t("chat:rateLimiting.limits.restricted"),
        };
      case "severe":
        return {
          color: "text-red-500",
          bgColor: "bg-red-500/10",
          borderColor: "border-red-500/30",
          icon: AlertTriangle,
          title: t("chat:rateLimiting.severelyLimited"),
          description: t("chat:rateLimiting.limits.severe"),
        };
      default:
        return {
          color: "text-blue-500",
          bgColor: "bg-blue-500/10",
          borderColor: "border-blue-500/30",
          icon: Clock,
          title: t("chat:rateLimiting.rateLimit"),
          description: t("chat:rateLimiting.pleaseWait"),
        };
    }
  };

  const levelInfo = getLevelInfo();
  const Icon = levelInfo.icon;

  const formatTime = (seconds: number) => {
    if (seconds < 60) {
      return `${seconds}s`;
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <AnimatePresence>
      {(!canSend || cooldownEnd) && (
        <motion.div
          animate={{ opacity: 1, y: 0, scale: 1 }}
          className={cn(
            "flex items-center gap-2 rounded-lg border px-3 py-2",
            levelInfo.bgColor,
            levelInfo.borderColor,
            className,
          )}
          exit={{ opacity: 0, y: -10, scale: 0.95 }}
          initial={{ opacity: 0, y: -10, scale: 0.95 }}
          transition={{ duration: 0.2, type: "spring", stiffness: 400, damping: 25 }}
        >
          <Icon className={cn("h-4 w-4", levelInfo.color)} />

          <div className="flex-1">
            <div className={cn("text-sm font-medium", levelInfo.color)}>{levelInfo.title}</div>
            {cooldownEnd && remainingCooldown > 0 ? (
              <div className="text-muted-foreground text-xs">
                {t("chat:rateLimiting.cooldownLabel")}{" "}
                {formatTime(Math.ceil(remainingCooldown / 1000))}
              </div>
            ) : timeLeft > 0 ? (
              <div className="text-muted-foreground text-xs">
                {t("chat:rateLimiting.nextMessageIn")} {formatTime(timeLeft)}
              </div>
            ) : (
              <div className="text-muted-foreground text-xs">{levelInfo.description}</div>
            )}
          </div>

          {/* Progress bar for cooldown */}
          {cooldownEnd && remainingCooldown > 0 && (
            <div className="bg-muted/30 h-1 w-16 overflow-hidden rounded-full">
              <motion.div
                animate={{
                  width: `${(remainingCooldown / (cooldownEnd - Date.now() + remainingCooldown)) * 100}%`,
                }}
                className={cn("h-full", levelInfo.bgColor.replace("/10", "/50"))}
                initial={{ width: "100%" }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
